import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import qs from 'query-string';

// Components
import Heading from '../Heading';
import Amount from '../Amount';
import Button from '../Button';
import SizeSelector from '../SizeSelector';
import Modal from '../Modal';
import AlcoholWarning from './AlcoholWarning';
import MetaOption from '../ProductCard/MetaOption';
import NavBar from '../NavBar';
import ProductCard from '../ProductCard';

// Router
import { getRoute } from '../../entry/routes';

// State
import { getSizes, getVariation } from '../../state/modules/catalog/utils';

// Utils
import {
  isPizza,
  isHot,
  isHalal,
  isVegeterian,
  isAlcohol,
  isNew,
  isHit,
} from '../../utils/catalog';

// Styles
import styles from './ProductCardCompact.styl';

const cx = classNames.bind(styles);

export default function ProductCardCompact(props) {
  const {
    good,
    className,
    stockRole,
    addToCartAction,
    removeIngredientAction,
    cancelRemovedIngredientsAction,
    type,
    isPromo,
    isLoadingStockApply,
    isHiddenActions,
  } = props;

  const { id, variations, alias, name, data, types, isAdding, good_type, isAdded, category } = good;

  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const categoriesAliases = useSelector(state => state.catalog.products.categoriesAliases);

  const { product } = qs.parse(location.search, { parseNumbers: true });

  const [size, setSize] = useState(variations[0]?.size?.value);
  const [isOpen, setIsOpen] = useState(false);

  // вариация исходя из выбранного размера
  const variation = getVariation(variations, size);
  // доступные размеры пиццы
  const sizes = getSizes(variations);

  const ingredients = variation.include_ingredients || [];
  const isMetaOptionsExist = isHot(types) || isHalal(types) || isVegeterian(types);
  const isBadgeExist = isNew(types) || isHit(types);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const hideModal = () => {
    if (stockRole) {
      setIsOpen(false);
    } else {
      history.push({ search: null });
    }
    setIsModalOpen(false);
  };

  const changeSize = newSize => {
    setSize(newSize);
  };

  const goToComboboxPage = () => {
    if (good_type === 'combo') {
      history.push(getRoute('comboboxAlias', alias));
    }
  };

  const onClick = async () => {
    if (isHiddenActions) {
      return;
    }

    if (isPizza(variation) && (variations.length > 1 || ingredients.length > 0)) {
      if (stockRole) {
        setIsOpen(true);
      } else {
        history.push({ search: qs.stringify({ product: id }) });
      }
      setIsModalOpen(true);
      return;
    }

    addToCartAction({ goodId: id, variation });
  };

  const openModalForPizza = () => {
    if (isHiddenActions) {
      return;
    }

    if (isPizza(variation) && (variations.length > 1 || ingredients.length > 0)) {
      if (stockRole) {
        setIsOpen(true);
      } else {
        history.push({ search: qs.stringify({ product: id }) });
      }
      setIsModalOpen(true);
    }
  };

  useEffect(() => {
    if (isAdded) {
      hideModal();
    }
  }, [isAdded]);

  return (
    <div className={cx('ProductCardCompact', className)}>
      <div style={{ display: 'flex', flexGrow: 1 }} onClick={openModalForPizza}>
        <picture
          className={cx('ProductCardCompact__picture', {
            ProductCardCompact__picture_pizza: isPizza(variation) && category === 'pizza',
          })}
        >
          {isBadgeExist && (
            <div className={cx('ProductCardCompact__badge')}>
              {isNew(types) ? 'New' : isHit(types) && 'Hit'}
            </div>
          )}
          <source srcSet={variation.image_cart_webp} type="image/webp" />
          <LazyLoadImage
            className={cx('ProductCardCompact__image')}
            alt={`${categoriesAliases[category]} ${name}`}
            src={variation.image_cart}
            threshold={300}
          />
        </picture>
        <div className={cx('ProductCardCompact__content')}>
          <Heading className={cx('ProductCardCompact__name')} level={3}>
            {name}
          </Heading>
          {Boolean(data) && <div className={cx('ProductCardCompact__description')}>{data}</div>}
          {isMetaOptionsExist && (
            <>
              {isHot(types) && (
                <MetaOption className={cx('ProductCardCompact__option')} type="hot" />
              )}
              {isHalal(types) && (
                <MetaOption className={cx('ProductCardCompact__option')} type="halal" />
              )}
              {isVegeterian(types) && (
                <MetaOption className={cx('ProductCardCompact__option')} type="vegetarian" />
              )}
            </>
          )}
          {!isPizza(variation) && sizes.length > 0 && (
            <SizeSelector
              className={cx('ProductCardCompact__sizes')}
              sizes={sizes}
              onChange={changeSize}
              currentSize={size}
            />
          )}
          <div className={cx('ProductCardCompact__footer')}>
            {type !== 'gift' && (
              <Amount
                className={cx('ProductCardCompact__price')}
                value={variation.old_price || variation.price}
                newValue={variation.price}
                size="s"
                variation={variation}
                prefix={isPizza(variation) && intl.formatMessage({ id: 'amount.prefix' })}
              />
            )}
            {good_type === 'combo' ? (
              <Button
                onClick={goToComboboxPage}
                className={cx('ProductCardCompact__button')}
                disabled={isHiddenActions}
                secondary
                small
              >
                {intl.formatMessage({ id: 'combobox.card.button' })}
              </Button>
            ) : (
              <Button
                className={cx('ProductCardCompact__button')}
                onClick={onClick}
                isLoading={isAdding}
                disabled={isHiddenActions || (isLoadingStockApply && !isAdding)}
                secondary
                small
              >
                {intl.formatMessage({
                  id:
                    variations.length === 1 && !ingredients.length
                      ? 'button.inCart'
                      : 'button.choose',
                })}
              </Button>
            )}
          </div>
        </div>
        {isAlcohol(types) && <AlcoholWarning />}
      </div>
      <Modal isOpen={stockRole ? isOpen : product === id} onClose={hideModal}>
        <NavBar
          view="secondary"
          title={intl.formatMessage({ id: 'link.goToHome' })}
          onBack={hideModal}
        />
        <ProductCard
          isLoadingStockApply={isLoadingStockApply}
          good={good}
          stockRole={stockRole}
          addToCartAction={addToCartAction}
          removeIngredientAction={removeIngredientAction}
          cancelRemovedIngredientsAction={cancelRemovedIngredientsAction}
          type={type}
          compact
          isModalOpenPhone={isModalOpen}
          isPromo={isPromo}
          isHiddenActions={isHiddenActions}
        />
      </Modal>
    </div>
  );
}

ProductCardCompact.defaultProps = {
  className: '',
  stockRole: '',
  type: 'good',
  isHiddenActions: false,
};

ProductCardCompact.propTypes = {
  className: PropTypes.string,
  stockRole: PropTypes.string,
  good: PropTypes.object.isRequired,
  addToCartAction: PropTypes.func.isRequired,
  removeIngredientAction: PropTypes.func.isRequired,
  cancelRemovedIngredientsAction: PropTypes.func.isRequired,
  type: PropTypes.string,
  isHiddenActions: PropTypes.bool,
};
